import React, { useEffect } from 'react';
import { Drawer, FormRow, Button } from 'components/common';
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from 'react-redux';
import { MdOutlineClose, MdSaveAs, MdOutlineSave, MdCallReceived, MdCallMade } from 'react-icons/md';
import AsyncSelect from 'react-select/async';
import Select, { components } from 'react-select';
import DatePicker from "react-datepicker";
import { adjustToNoon } from 'helpers/utils';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { getItemDimensions, getWarehouses } from 'redux/erp/itemSlice';
import { getListJobs } from 'redux/crm/shipment/packingListSlice';
import { INPUT_CLS, TEXTAREA_CLS } from 'helpers/formStyles';

const FORM_ID = 'inventory-transaction-form';

/**
 * Group flat warehouse data into react-select groups: parent warehouses become
 * (non-selectable) group headings, their children become the selectable options.
 * A parent with no children is kept as a single selectable option in its own group.
 */
const transformWarehouseData = (warehouses) => {
  if (!Array.isArray(warehouses)) return [];

  const parents = [];
  const children = [];
  warehouses.forEach((item) => {
    const w = item.attributes;
    if (!w.parent) parents.push(w);
    else children.push(w);
  });

  return parents.map((parent) => {
    const kids = children.filter((c) => c.parent === parent.name);
    return {
      label: parent.name,
      options: kids.length
        ? kids.map((c) => ({ value: c.id, label: c.name }))
        : [{ value: parent.id, label: parent.name }],
    };
  });
};

// react-select styling — clean group headings + comfortable option rows.
const warehouseSelectStyles = {
  groupHeading: (provided) => ({
    ...provided,
    fontWeight: 700,
    color: '#334155',
    textTransform: 'none',
    fontSize: '13px',
    padding: '6px 12px',
    background: '#f8fafc',
  }),
  option: (provided, state) => ({
    ...provided,
    paddingLeft: '28px',
    cursor: 'pointer',
    backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#eff6ff' : 'white',
    color: state.isSelected ? 'white' : '#1e293b',
  }),
  menu: (provided) => ({ ...provided, zIndex: 9999 }),
};

const TransactionForm = ({ open, setOpen, record, title, onSave }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue, getValues } = useForm();
  const dispatch = useDispatch();

  useEffect(() => {
    setValue("trans_date", adjustToNoon(new Date()));
    setValue("trans_type", "in"); // Default to 'In'

    if (record) {
      Object.entries(record).forEach(([key, value]) => {
        if (key === "trans_date") setValue(key, new Date(value));
        else if (key === "warehouse_id" && record.warehouse) {
          setValue(key, { value: record.warehouse_id, label: record.warehouse.name });
        } else setValue(key, value);
      });
    }
  }, [setValue, record, open]);

  const loadItemOptions = async (inputValue) => {
    try {
      const params = {
        startRow: 0,
        endRow: 50,
        filterModel: JSON.stringify({ active: { filterType: 'set', values: [true] } }),
      };
      if (inputValue && inputValue.length > 2) {
        params.filterModel = JSON.stringify({
          active: { filterType: 'set', values: [true] },
          name: { filterType: 'text', type: 'contains', filter: inputValue },
        });
      }
      const response = await dispatch(getItemDimensions(params)).unwrap();
      const data = response.rows;
      return Array.isArray(data)
        ? data.map((item) => item.attributes).map((item) => ({ value: item.id, label: item.name }))
        : [];
    } catch (error) {
      return [];
    }
  };

  const loadWarehouseOptions = async (inputValue) => {
    try {
      const params = { startRow: 0, endRow: 100 };
      if (inputValue && inputValue.length > 2) params.name_cont = inputValue;
      const response = await dispatch(getWarehouses(params)).unwrap();
      return transformWarehouseData(response.rows);
    } catch (error) {
      console.error('Error loading warehouses:', error);
      return [];
    }
  };

  const loadJobOptions = async (inputValue) => {
    try {
      const params = { page: 1, per_page: 50 };
      if (inputValue && inputValue.length > 2) params.j_number_cont = inputValue;
      const response = await dispatch(getListJobs(params)).unwrap();
      const data = response.data;
      const jobOptions = Array.isArray(data)
        ? data.map((item) => ({ value: item.j_number, label: item.j_number }))
        : [];
      return [...jobOptions, { value: 'other', label: 'Other' }];
    } catch (error) {
      return [];
    }
  };

  const onSubmit = async (data, event) => {
    const actionType = event.nativeEvent.submitter.value;
    const saveAndClose = actionType === "saveAndClose";
    const payload = { ...data, warehouse_id: data.warehouse_id?.value || null };
    try {
      await onSave(payload);
      saveAndClose ? handleClose() : resetForm();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const resetForm = () => {
    setValue("weight", "");
    setValue("building", "");
    setValue("remark", "");
  };

  const handleClose = () => setOpen(false);

  const defaultItemOption = record && record.dimension_id && record.dimension?.name
    ? { value: record.dimension_id, label: record.dimension.name }
    : null;

  const defaultJobOption = record && record.job_number
    ? { value: record.job_number, label: record.job_number }
    : null;

  const transTypeOptions = [
    { value: 'in',  label: 'In',  icon: <MdCallReceived className="tw-mr-1" style={{ color: '#28a745' }} /> },
    { value: 'out', label: 'Out', icon: <MdCallMade className="tw-mr-1" style={{ color: '#dc3545' }} /> },
  ];

  const CustomOption = ({ data, ...props }) => {
    const { Option } = components;
    return (
      <Option {...props}>
        <div className="tw-flex tw-items-center">{data.icon}<span>{data.label}</span></div>
      </Option>
    );
  };
  CustomOption.propTypes = { data: PropTypes.object.isRequired };

  const CustomSingleValue = ({ data, ...props }) => {
    const { SingleValue } = components;
    return (
      <SingleValue {...props}>
        <div className="tw-flex tw-items-center">{data.icon}<span>{data.label}</span></div>
      </SingleValue>
    );
  };
  CustomSingleValue.propTypes = { data: PropTypes.object.isRequired };

  const footer = (
    <div className="tw-flex tw-items-center tw-justify-end tw-gap-2 tw-w-full">
      {!record && (
        <Button type="submit" form={FORM_ID} name="action" value="saveAndContinue" variant="secondary" size="sm">
          <MdSaveAs size={14} /> Save and continue
        </Button>
      )}
      <Button type="submit" form={FORM_ID} name="action" value="saveAndClose" variant="primary" size="sm">
        <MdOutlineSave size={14} /> {record ? "Update" : "Save and Close"}
      </Button>
      <Button type="button" onClick={handleClose} variant="ghost" size="sm">
        <MdOutlineClose size={14} /> Close
      </Button>
    </div>
  );

  return (
    <Drawer show={open} onHide={handleClose} title={title} backdropClick={false} width="50%" minWidth="480px" footer={footer}>
      <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
        <FormRow label="Item name" required error={errors.dimension_id?.message}>
          <Controller
            control={control}
            name="dimension_id"
            rules={{ required: "Item name is required" }}
            render={() => (
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadItemOptions}
                defaultValue={defaultItemOption}
                closeMenuOnSelect
                placeholder="Select..."
                classNamePrefix="react-select"
                onChange={(o) => setValue("dimension_id", o ? o.value : null, { shouldValidate: true })}
              />
            )}
          />
        </FormRow>

        <FormRow label="Trans date" required error={errors.trans_date?.message}>
          <Controller
            control={control}
            name="trans_date"
            rules={{ required: "Trans date is required" }}
            render={({ field }) => (
              <DatePicker
                selected={field.value}
                onChange={d => field.onChange(adjustToNoon(d))}
                className={INPUT_CLS}
                placeholderText="dd-MMM-YYYY"
                dateFormat="dd-MMM-yyyy"
                fixedHeight
                todayButton="Today"
              />
            )}
          />
        </FormRow>

        <FormRow label="Trans Type" required error={errors.trans_type?.message}>
          <Controller
            control={control}
            name="trans_type"
            rules={{ required: 'Trans type is required' }}
            render={({ field }) => (
              <Select
                {...field}
                options={transTypeOptions}
                value={transTypeOptions.find((o) => o.value === field.value)}
                placeholder="Select..."
                classNamePrefix="react-select"
                onChange={(o) => field.onChange(o.value)}
                components={{ Option: CustomOption, SingleValue: CustomSingleValue }}
              />
            )}
          />
        </FormRow>

        <FormRow label="Qty" required error={errors.qty?.message}>
          <input
            type="number"
            placeholder="Qty"
            className={INPUT_CLS}
            {...register("qty", {
              required: { value: true, message: 'Qty is required' },
              validate: (value) => {
                const qty = parseFloat(value);
                const transType = getValues("trans_type");
                if (transType === "out") return qty < 0 || "Qty must be negative for 'Out' transactions";
                if (transType === "in") return qty > 0 || "Qty must be positive for 'In' transactions";
                return true;
              },
            })}
          />
        </FormRow>

        <FormRow label="Job Number" required error={errors.job_number?.message}>
          <Controller
            control={control}
            name="job_number"
            rules={{ required: "Job number is required" }}
            render={() => (
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadJobOptions}
                defaultValue={defaultJobOption}
                closeMenuOnSelect
                placeholder="Select..."
                classNamePrefix="react-select"
                onChange={(o) => setValue("job_number", o ? o.value : null, { shouldValidate: true })}
              />
            )}
          />
        </FormRow>

        <FormRow label="Warehouse" required error={errors.warehouse_id?.message}>
          <Controller
            control={control}
            name="warehouse_id"
            rules={{ required: "Warehouse is required" }}
            render={({ field }) => (
              <AsyncSelect
                {...field}
                cacheOptions
                defaultOptions
                loadOptions={loadWarehouseOptions}
                closeMenuOnSelect
                placeholder="Select warehouse..."
                classNamePrefix="react-select"
                styles={warehouseSelectStyles}
                onChange={(o) => setValue("warehouse_id", o, { shouldValidate: true })}
                isClearable
              />
            )}
          />
        </FormRow>

        <FormRow label="Journal Number" error={errors.journal_number?.message}>
          <input
            placeholder="Journal Number"
            className={INPUT_CLS}
            {...register("journal_number")}
          />
        </FormRow>

        <FormRow label="Remark">
          <textarea
            rows={5}
            placeholder="Remark"
            className={TEXTAREA_CLS}
            {...register("remark")}
          />
        </FormRow>
      </form>
    </Drawer>
  );
};

TransactionForm.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  record: PropTypes.object,
  title: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default TransactionForm;
